/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { FileText } from 'lucide-react';
import { useCallback, useMemo, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Badge, type BadgeTone } from '../../components/ui/badge.js';
import { Button } from '../../components/ui/button.js';
import { Modal } from '../../components/ui/modal.js';
import { type Session, type SessionStatus } from '../../lib/api/sessions.js';
import { useRooms } from '../../lib/queries/rooms.js';
import { formatIst } from '../../lib/utils/datetime.js';

const STATUS_TONE: Readonly<Record<SessionStatus, BadgeTone>> = {
  scheduled: 'violet',
  in_progress: 'amber',
  completed: 'green',
  cancelled: 'slate',
  no_show: 'slate',
  client_late: 'amber',
  consultant_attended_client_absent: 'rose',
};

/** Scheduled duration as "N min" (Figma Duration column); "—" when open-ended. */
function durationLabel(s: Session): string {
  if (s.scheduledEnd === null) {
    return '—';
  }
  const mins = Math.round(
    (new Date(s.scheduledEnd).getTime() - new Date(s.scheduledStart).getTime()) / 60000,
  );
  return mins > 0 ? `${String(mins)} min` : '—';
}

/** Read-only Session Details (Figma "My session 2"). Reused by the schedule grid. */
export function SessionDetails({
  session,
  clientName,
  roomName,
}: {
  readonly session: Session;
  readonly clientName: string;
  readonly roomName: string;
}): JSX.Element {
  const { t } = useTranslation();
  const fields: readonly [string, string][] = [
    [t('therapist.sessions.colClient'), clientName],
    [t('therapist.sessions.colType'), session.sessionType ?? '—'],
    [t('therapist.sessions.colTime'), formatIst(session.scheduledStart, 'EEE d MMM, h:mm a')],
    [t('therapist.sessions.colDuration'), durationLabel(session)],
    [t('therapist.sessions.colRoom'), roomName],
    [t('therapist.sessions.colStatus'), t(`status.${session.status}`)],
  ];
  return (
    <div className="grid grid-cols-2 gap-3">
      {fields.map(([label, value]) => (
        <div key={label} className="rounded-xl bg-slate-50 p-3 dark:bg-slate-800/50">
          <p className="text-xs text-slate-400">{label}</p>
          <p className="mt-0.5 text-sm font-semibold text-slate-800 dark:text-slate-100">{value}</p>
        </div>
      ))}
    </div>
  );
}

interface RowProps {
  readonly session: Session;
  readonly clientName: string;
  readonly roomName: string;
  readonly onTransition: (id: string, action: 'start' | 'complete' | 'no-show') => void;
  readonly onCancel: (id: string) => void;
  readonly onReschedule: (session: Session) => void;
  readonly onRate: (session: Session) => void;
  readonly onDetails: (session: Session) => void;
}

function SessionRow({
  session,
  clientName,
  roomName,
  onTransition,
  onCancel,
  onReschedule,
  onRate,
  onDetails,
}: RowProps): JSX.Element {
  const { t } = useTranslation();
  const start = useCallback(() => {
    onTransition(session.id, 'start');
  }, [onTransition, session.id]);
  const complete = useCallback(() => {
    onTransition(session.id, 'complete');
  }, [onTransition, session.id]);
  const cancel = useCallback(() => {
    onCancel(session.id);
  }, [onCancel, session.id]);
  const reschedule = useCallback(() => {
    onReschedule(session);
  }, [onReschedule, session]);
  const rate = useCallback(() => {
    onRate(session);
  }, [onRate, session]);
  const details = useCallback(() => {
    onDetails(session);
  }, [onDetails, session]);
  const live = session.status === 'scheduled' || session.status === 'in_progress';

  return (
    <tr className="hover:bg-slate-50">
      <td className="px-5 py-3 text-slate-600">
        {formatIst(session.scheduledStart, 'dd MMM, h:mm a')}
      </td>
      <td className="px-5 py-3">
        <button
          type="button"
          onClick={details}
          className="focus-ring font-medium text-slate-800 hover:text-[var(--accent)]"
        >
          {clientName}
        </button>
      </td>
      <td className="px-5 py-3 text-slate-600">{session.sessionType ?? '—'}</td>
      <td className="px-5 py-3 text-slate-600">{durationLabel(session)}</td>
      <td className="px-5 py-3 text-slate-600">{roomName}</td>
      <td className="px-5 py-3">
        <Badge label={t(`status.${session.status}`)} tone={STATUS_TONE[session.status]} />
      </td>
      <td className="px-5 py-3">
        <div className="flex flex-wrap justify-end gap-2">
          {session.status === 'scheduled' && (
            <Button size="sm" onClick={start}>
              {t('therapist.sessions.start')}
            </Button>
          )}
          {session.status === 'scheduled' && (
            <Button size="sm" variant="outline" onClick={reschedule}>
              {t('therapist.sessions.reschedule')}
            </Button>
          )}
          {session.status === 'in_progress' && (
            <Button size="sm" onClick={complete}>
              {t('therapist.sessions.complete')}
            </Button>
          )}
          {(session.status === 'in_progress' || session.status === 'completed') && (
            <Link
              to={`/therapist/sessions/${session.id}/note`}
              className="focus-ring inline-flex h-9 items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              <FileText className="h-4 w-4" aria-hidden="true" />
              {t('therapist.sessions.note')}
            </Link>
          )}
          {session.status === 'completed' && (
            <Button size="sm" variant="outline" onClick={rate}>
              {t('therapist.sessions.rate')}
            </Button>
          )}
          {live && (
            <Button size="sm" variant="ghost" onClick={cancel}>
              {t('therapist.sessions.cancel')}
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}

interface SessionTableProps {
  readonly sessions: readonly Session[];
  readonly clientNames: ReadonlyMap<string, string>;
  readonly onTransition: (id: string, action: 'start' | 'complete' | 'no-show') => void;
  readonly onCancel: (id: string) => void;
  readonly onReschedule: (session: Session) => void;
  readonly onRate: (session: Session) => void;
}

const COLS = ['colTime', 'colClient', 'colType', 'colDuration', 'colRoom', 'colStatus'] as const;

/** Therapist My-Sessions table (Figma) + the read-only Session-Details modal. */
export function SessionTable({
  sessions,
  clientNames,
  onTransition,
  onCancel,
  onReschedule,
  onRate,
}: SessionTableProps): JSX.Element {
  const { t } = useTranslation();
  const rooms = useRooms();
  const [details, setDetails] = useState<Session | null>(null);
  const roomNames = useMemo(
    () => new Map((rooms.data ?? []).map((r) => [r.id, r.name])),
    [rooms.data],
  );
  const roomOf = useCallback(
    (s: Session): string => (s.roomId !== null ? (roomNames.get(s.roomId) ?? '—') : '—'),
    [roomNames],
  );
  const onDetails = useCallback((s: Session) => {
    setDetails(s);
  }, []);
  const onDetailsOpenChange = useCallback((open: boolean) => {
    if (!open) {
      setDetails(null);
    }
  }, []);

  return (
    <>
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              {COLS.map((c) => (
                <th key={c} className="px-5 py-3 font-semibold">
                  {t(`therapist.sessions.${c}`)}
                </th>
              ))}
              <th className="px-5 py-3 text-right font-semibold">
                {t('therapist.sessions.colActions')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {sessions.map((session) => (
              <SessionRow
                key={session.id}
                session={session}
                clientName={clientNames.get(session.clientId) ?? '—'}
                roomName={roomOf(session)}
                onTransition={onTransition}
                onCancel={onCancel}
                onReschedule={onReschedule}
                onRate={onRate}
                onDetails={onDetails}
              />
            ))}
          </tbody>
        </table>
      </div>
      <Modal
        open={details !== null}
        onOpenChange={onDetailsOpenChange}
        title={t('therapist.sessions.detailsTitle')}
        description={details !== null ? (clientNames.get(details.clientId) ?? '') : undefined}
        closeLabel={t('common.cancel')}
      >
        {details !== null && (
          <SessionDetails
            session={details}
            clientName={clientNames.get(details.clientId) ?? '—'}
            roomName={roomOf(details)}
          />
        )}
      </Modal>
    </>
  );
}
